import type { QueryClient } from '@tanstack/react-query';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';
import type { PetitionAngleData } from '#src/app/shared/api/socialStarter';
import { formatAngleDescriptionForSapEditor } from '#src/app/shared/utils/socialStarter';

import {
	createPersistentDraftContext,
	generateImagePreparation,
	getDraftImage,
	type ImagePreparation,
	reportPrewarmPersistenceError,
	type DraftContext,
} from './imagePreparation';

type PrewarmContext = DraftContext &
	Readonly<{
		socialStarterId: string;
		utilityContext: UtilityContext;
	}>;

type DraftLookupResult = Readonly<{ succeeded: true; draft: PetitionDraft | null }> | Readonly<{ succeeded: false }>;

async function lookupDraft(context: PrewarmContext): Promise<DraftLookupResult> {
	try {
		return { succeeded: true, draft: await context.draftClient.getDraft(context.authorId) };
	} catch (error) {
		reportPrewarmPersistenceError(context.utilityContext, error);
		return { succeeded: false };
	}
}

function getReusableImagePreparation(
	existingDraft: PetitionDraft | null,
	context: PrewarmContext,
): ImagePreparation | null {
	if (!existingDraft?.metadata || existingDraft.metadata.angleToken !== context.socialStarterId) return null;

	const existingImage = getDraftImage(existingDraft);
	const existingImageJobId = existingDraft.metadata.imageJobId ?? null;
	if (!existingImage && !existingImageJobId) return null;

	return {
		imageGenerationCount: existingDraft.metadata.imageGenerationCount ?? 0,
		imageJobId: existingImage ? null : existingImageJobId,
		reusableDraftContext: context,
	};
}

/**
 * Reserves the browser's SAP draft slot before claim so an image job can survive a revisit.
 */
async function createSpeculativeDraft(context: PrewarmContext, draft: PetitionAngleData): Promise<void> {
	await context.draftClient.createDraft(context.authorId, {
		type: 'user_draft',
		petitionDraft: {
			ask: draft.ask,
			description: formatAngleDescriptionForSapEditor(draft.description),
		},
		metadata: {
			angleToken: context.socialStarterId,
			imageJobId: null,
			imageGenerationCount: 0,
		},
	});
}

async function ensureReusableDraft(
	existingDraft: PetitionDraft | null,
	imageDraft: PetitionAngleData,
	context: PrewarmContext,
): Promise<boolean> {
	// A single browser draft slot is reusable only when it already belongs to this Social Starter.
	if (existingDraft) return existingDraft.metadata?.angleToken === context.socialStarterId;

	try {
		await createSpeculativeDraft(context, imageDraft);
		return true;
	} catch (error) {
		reportPrewarmPersistenceError(context.utilityContext, error);
		return false;
	}
}

async function persistPrewarmedImageJob(preparation: ImagePreparation, context: PrewarmContext): Promise<void> {
	try {
		const currentDraft = await context.draftClient.getDraft(context.authorId);
		// The draft may change while generation starts, so recheck ownership before attaching the job.
		if (currentDraft?.metadata?.angleToken !== context.socialStarterId) return;

		await context.draftClient.updateDraft(context.authorId, {
			metadata: {
				...currentDraft.metadata,
				angleToken: context.socialStarterId,
				imageJobId: preparation.imageJobId,
				imageGenerationCount: preparation.imageGenerationCount,
			},
		});
	} catch (error) {
		reportPrewarmPersistenceError(context.utilityContext, error);
	}
}

async function generateAndPersistPrewarmedImage(
	imageDraft: PetitionAngleData,
	existingDraft: PetitionDraft | null,
	canReuseDraft: boolean,
	context: PrewarmContext,
): Promise<ImagePreparation> {
	const generated = await generateImagePreparation(imageDraft, context.utilityContext);
	const preparation: ImagePreparation = {
		...generated,
		imageGenerationCount:
			(canReuseDraft ? (existingDraft?.metadata?.imageGenerationCount ?? 0) : 0) + generated.imageGenerationCount,
		reusableDraftContext: canReuseDraft ? context : null,
	};

	if (canReuseDraft) await persistPrewarmedImageJob(preparation, context);
	return preparation;
}

/**
 * Implements browser-persisted prewarming when preferences storage is permitted.
 */
export async function preparePersistedPrewarmedImage(
	imageDraft: PetitionAngleData,
	socialStarterId: string,
	queryClient: QueryClient,
	utilityContext: UtilityContext,
): Promise<ImagePreparation> {
	const context = {
		...createPersistentDraftContext(queryClient),
		socialStarterId,
		utilityContext,
	};
	const draftLookup = await lookupDraft(context);
	if (!draftLookup.succeeded) return generateImagePreparation(imageDraft, utilityContext);

	const reusablePreparation = getReusableImagePreparation(draftLookup.draft, context);
	if (reusablePreparation) return reusablePreparation;

	const canReuseDraft = await ensureReusableDraft(draftLookup.draft, imageDraft, context);
	return generateAndPersistPrewarmedImage(imageDraft, draftLookup.draft, canReuseDraft, context);
}
